/**
 * Frame Renderer
 *
 * Renders video frames with clips, transformations, and burned-in subtitles
 */

import { Clip, Subtitle, SubtitleStyle, Transform, Track } from './types';
import { getClipExportSourceUrl } from './utils/clip-helpers';
import { easeInBack, easeOutElastic, easeInOutCubic } from './utils';

interface MediaElement {
  clip: Clip;
  element: HTMLVideoElement | HTMLImageElement;
  loaded: boolean;
}

/**
 * Frame Renderer class
 */
export class FrameRenderer {
  private mediaElements = new Map<string, MediaElement>();
  private canvasWidth: number;
  private canvasHeight: number;
  private fontScaleFactor: number;
  // Résolution de référence du preview (pour le scaling des polices)
  private static readonly PREVIEW_REFERENCE_WIDTH = 1280;

  constructor(width: number, height: number) {
    this.canvasWidth = width;
    this.canvasHeight = height;
    // Calculer le facteur de scale pour les polices en fonction de la résolution
    this.fontScaleFactor = width / FrameRenderer.PREVIEW_REFERENCE_WIDTH;
  }

  /**
   * Preload all media for clips
   */
  async preloadMedia(clips: Clip[]): Promise<void> {
    const loadPromises: Promise<void>[] = [];

    for (const clip of clips) {
      if (clip.type === 'video' && getClipExportSourceUrl(clip)) {
        loadPromises.push(this.loadVideo(clip));
      } else if (clip.type === 'image' && (clip.localUrl || clip.url)) {
        loadPromises.push(this.loadImage(clip));
      } else if ((clip.type === 'synthe' || clip.type === 'panel') && clip.syntheData) {
        // Recursively preload nested clips
        loadPromises.push(this.preloadMedia(clip.syntheData.clips));
      }
    }

    await Promise.all(loadPromises);
  }

  /**
   * Load video element
   */
  private async loadVideo(clip: Clip): Promise<void> {
    return new Promise((resolve, reject) => {
      const sourceUrl = getClipExportSourceUrl(clip);
      if (!sourceUrl) {
        reject(new Error(`No playable video source for ${clip.name}`));
        return;
      }

      const video = window.document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'auto'; // Need full data for export rendering, not just metadata
      video.muted = true;
      video.playsInline = true;

      const mediaElement: MediaElement = {
        clip,
        element: video,
        loaded: false,
      };

      let watchdogTimer: NodeJS.Timeout;

      const clearWatchdog = () => {
        if (watchdogTimer) clearTimeout(watchdogTimer);
      };

      const resetWatchdog = (timeoutMs: number = 30000, reason: string = 'stalled') => {
        clearWatchdog();
        watchdogTimer = setTimeout(() => {
          if (!mediaElement.loaded) {
            // Fallback only when the first frame is actually decodable.
            if (video.readyState >= 2) {
              console.warn(`[FrameRenderer] Watchdog timeout (${reason}) for ${clip.name} but readyState >= 2. Proceeding.`);
              handleReady();
            } else {
              console.error(`[FrameRenderer] Video loading timeout (${reason}): ${clip.name}`);
              reject(new Error(`Video loading timeout: ${clip.name}`));
            }
          }
        }, timeoutMs);
      };

      const handleReady = () => {
        if (!mediaElement.loaded) {
          mediaElement.loaded = true;
          clearWatchdog();
          resolve();
        }
      };

      video.addEventListener('loadeddata', () => {
        handleReady();
      }, { once: true });

      video.addEventListener('canplaythrough', () => {
        handleReady();
      }, { once: true });

      video.addEventListener('error', (e) => {
        clearWatchdog();
        console.error(`[FrameRenderer] Failed to load video: ${sourceUrl}`, e);
        reject(new Error(`Failed to load video: ${clip.name}`));
      });

      // Keep watchdog alive while actively downloading data
      video.addEventListener('progress', () => {
        if (!mediaElement.loaded) {
          resetWatchdog(30000, 'download paused'); // Give 30s after the last progress event
        }
      });

      // Browser voluntarily stopped downloading (e.g. buffered enough or network suspended)
      video.addEventListener('suspend', () => {
        if (!mediaElement.loaded && video.readyState >= 2) {
          console.log(`[FrameRenderer] Video download suspended for ${clip.name}, first frame available. Proceeding.`);
          handleReady();
        }
      });

      video.addEventListener('stalled', () => {
        console.warn(`[FrameRenderer] Download stalled for ${clip.name}`);
        // the watchdog will handle the timeout if progress doesn't resume
      });

      // Start the initial watchdog
      resetWatchdog(45000, 'initial load');

      video.src = sourceUrl;
      video.load();

      this.mediaElements.set(clip.id, mediaElement);
    });
  }

  /**
   * Load image element
   */
  private async loadImage(clip: Clip): Promise<void> {
    return new Promise((resolve, reject) => {
      const imageUrl = clip.localUrl || clip.url;
      if (!imageUrl) {
        reject(new Error(`No image source for ${clip.name}`));
        return;
      }

      // FIX: Use `(window as any).Image` to access the Image constructor.
      const img = new ((window as any).Image)();
      img.crossOrigin = 'anonymous';

      const mediaElement: MediaElement = {
        clip,
        element: img,
        loaded: false,
      };

      img.addEventListener('load', () => {
        mediaElement.loaded = true;
        resolve();
      });

      img.addEventListener('error', () => {
        console.error(`Failed to load image: ${imageUrl}`);
        reject(new Error(`Failed to load image: ${clip.name}`));
      });

      img.src = imageUrl;

      this.mediaElements.set(clip.id, mediaElement);
    });
  }

  /**
   * Render a frame at a specific time
   */
  async renderFrame(
    // FIX: Allow OffscreenCanvasRenderingContext2D to support WebCodecsExporter.
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    currentTime: number,
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle,
    scaleMode: 'fit' | 'fill' = 'fill',
    clearCanvas: boolean = true
  ): Promise<void> {
    // Clear canvas to be transparent
    if (clearCanvas) {
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }

    // Get all active and visible clips
    const allVisibleClips = clips.filter(c => {
      const track = tracks.find(t => t.id === c.trackId);
      return track?.isVisible && currentTime >= c.start && currentTime < c.start + c.duration;
    });

    // Separate overlay clips (on track 't1') from main content clips
    const overlayClips = allVisibleClips.filter(c => c.trackId === 't1');
    const contentClips = allVisibleClips.filter(c => c.trackId !== 't1');

    const activeSplitClip = contentClips.find(c => c.trackId === 's1');

    // RENDER MAIN CONTENT (WITH SPLIT SCREEN OR ZOOM)
    if (activeSplitClip) {
      // --- SPLIT SCREEN LOGIC ---
      const animationDuration = 1.0;
      const timeIntoClip = currentTime - activeSplitClip.start;
      const timeBeforeEnd = (activeSplitClip.start + activeSplitClip.duration) - currentTime;
      let progress = 1.0;

      if (timeIntoClip < animationDuration) {
        progress = timeIntoClip / animationDuration;
      } else if (timeBeforeEnd < animationDuration) {
        progress = timeBeforeEnd / animationDuration;
      }
      const finalProgress = easeInOutCubic(progress);

      // --- RENDER LEFT SIDE ---
      const clipsForLeft = contentClips.filter(c => c.id !== activeSplitClip.id && c.trackId !== 's1');
      ctx.save();
      const leftScale = 1.0 - (0.05 * finalProgress);
      const leftTranslateX = (-this.canvasWidth / 4) * finalProgress;
      ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      ctx.scale(leftScale, leftScale);
      ctx.translate(-this.canvasWidth / 2 + leftTranslateX, -this.canvasHeight / 2);
      await this.renderClipsWithCamera(ctx, clipsForLeft, contentClips, currentTime, tracks, scaleMode);
      ctx.restore();

      // --- RENDER RIGHT SIDE ---
      ctx.save();
      const panelLeadingEdgeX = this.canvasWidth - (this.canvasWidth / 2) * finalProgress;
      const rightClipPath = new Path2D();
      rightClipPath.rect(panelLeadingEdgeX, 0, this.canvasWidth - panelLeadingEdgeX, this.canvasHeight);
      ctx.clip(rightClipPath);
      const contentTranslationX = panelLeadingEdgeX - (this.canvasWidth / 2);
      ctx.translate(contentTranslationX, 0);
      const rightScale = 1.0 - (0.05 * finalProgress);
      const rightTranslateX = (this.canvasWidth / 4) * finalProgress;
      ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      ctx.scale(rightScale, rightScale);
      ctx.translate(-this.canvasWidth / 2 + rightTranslateX, -this.canvasHeight / 2);
      await this.renderClipsWithCamera(ctx, [activeSplitClip], contentClips, currentTime, tracks, scaleMode);
      ctx.restore();

    } else {
      // --- NORMAL RENDER LOGIC ---
      await this.renderClipsWithCamera(ctx, contentClips, contentClips, currentTime, tracks, scaleMode);
    }

    // RENDER OVERLAYS (on top of everything else)
    for (const clip of overlayClips) {
      await this.renderClip(ctx, clip, currentTime, tracks);
    }

    // Render subtitles last
    this.renderSubtitles(ctx, clips, currentTime, activeSubtitleLanguage, subtitleStyle);
  }

  private async renderClipsWithCamera(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clipsToRender: Clip[],
    allContentClips: Clip[],
    currentTime: number,
    tracks: Track[],
    scaleMode: 'fit' | 'fill' = 'fill'
  ) {
    const activeZoomClip = allContentClips.find(c => c.type === 'zoom');

    ctx.save();
    if (activeZoomClip && activeZoomClip.transform && activeZoomClip.endTransform) {
      const { transform: defaultTransform, endTransform: targetTransform, duration, start } = activeZoomClip;
      const timeIntoClip = currentTime - start;
      const animationTime = 1.0;
      const inDuration = Math.min(animationTime, duration / 2);
      const outStartTime = duration - inDuration;

      let progress = 0;
      if (timeIntoClip < inDuration) {
        progress = easeInOutCubic(timeIntoClip / inDuration);
      } else if (timeIntoClip > outStartTime && outStartTime > inDuration) {
        progress = 1 - easeInOutCubic((timeIntoClip - outStartTime) / inDuration);
      } else if (timeIntoClip >= inDuration && timeIntoClip <= outStartTime) {
        progress = 1;
      }

      const camX = defaultTransform.x + (targetTransform.x - defaultTransform.x) * progress;
      const camY = defaultTransform.y + (targetTransform.y - defaultTransform.y) * progress;
      const camScale = defaultTransform.scale + (targetTransform.scale - defaultTransform.scale) * progress;
      const camRot = defaultTransform.rotation + (targetTransform.rotation - defaultTransform.rotation) * progress;

      ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      if (camScale !== 0) ctx.scale(1 / camScale, 1 / camScale);
      ctx.rotate(camRot * Math.PI / 180);
      ctx.translate(-this.canvasWidth / 2 - camX, -this.canvasHeight / 2 - camY);
    }

    const sortedClips = [...clipsToRender].sort((a, b) => tracks.findIndex(t => t.id === b.trackId) - tracks.findIndex(t => t.id === a.trackId));
    for (const clip of sortedClips) {
      await this.renderClip(ctx, clip, currentTime, tracks, scaleMode);
    }
    ctx.restore();
  }


  /**
   * Render a single clip
   */
  public async renderClip(
    // FIX: Allow OffscreenCanvasRenderingContext2D to support WebCodecsExporter.
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clip: Clip,
    currentTime: number,
    tracks: Track[],
    scaleMode: 'fit' | 'fill' = 'fill'
  ): Promise<void> {
    const relativeTime = currentTime - clip.start;

    // Calculate opacity with fades
    let opacity = clip.opacity;
    if (relativeTime < clip.fadeInDuration) {
      opacity *= relativeTime / clip.fadeInDuration;
    }
    const fadeOutStart = clip.duration - clip.fadeOutDuration;
    if (relativeTime > fadeOutStart) {
      opacity *= (clip.duration - relativeTime) / clip.fadeOutDuration;
    }

    if (opacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = opacity;

    if (clip.type === 'video') {
      await this.renderVideoClip(ctx, clip, currentTime, scaleMode);
    } else if (clip.type === 'image') {
      this.renderImageClip(ctx, clip, currentTime, scaleMode);
    } else if (clip.type === 'text') {
      this.renderTextClip(ctx, clip);
    } else if ((clip.type === 'synthe' || clip.type === 'panel') && clip.syntheData) {
      await this.renderSyntheClip(ctx, clip, currentTime);
    }

    ctx.restore();
  }

  /**
   * Render a complex synthe or panel clip with nested media
   */
  private async renderSyntheClip(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    parentClip: Clip,
    currentTime: number
  ): Promise<void> {
    if (!parentClip.syntheData) return;

    const { clips, tracks, backgroundColor } = parentClip.syntheData;
    const relativeTime = currentTime - parentClip.start;

    // Draw background for panels
    if (parentClip.type === 'panel' && backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    }

    // Sort clips by track order (back to front)
    const sortedClips = [...clips].sort((a, b) => {
      const trackIdxA = tracks.findIndex(t => t.id === a.trackId);
      const trackIdxB = tracks.findIndex(t => t.id === b.trackId);
      return trackIdxB - trackIdxA;
    });

    for (const nestedClip of sortedClips) {
      const nestedVisible = relativeTime >= nestedClip.start && relativeTime < nestedClip.start + nestedClip.duration;
      if (!nestedVisible) continue;

      // Render nested clip (recursive call)
      await this.renderClip(ctx, nestedClip, relativeTime, tracks);
    }
  }

  /**
   * Render video clip
   */
  private async renderVideoClip(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clip: Clip,
    currentTime: number,
    scaleMode: 'fit' | 'fill' = 'fill'
  ): Promise<void> {
    const mediaElement = this.mediaElements.get(clip.id);
    if (!mediaElement || !mediaElement.loaded) return;

    const video = mediaElement.element as HTMLVideoElement;
    const speed = clip.playbackRate || 1;
    const clipTime = ((currentTime - clip.start) * speed) + clip.trimStart;

    // Only seek if the video time is significantly different
    const needsSeek = Math.abs(video.currentTime - clipTime) > 0.01;
    if (needsSeek) {
      video.currentTime = clipTime;

      await new Promise<void>((resolve) => {
        // FIX: Increase timeout from 2s to 10s for long videos (e.g., 1h30)
        // Seeking in large video files can take longer, especially when jumping to distant timestamps
        const timeout = setTimeout(() => {
          console.warn(`Video seek timed out for ${clip.name} at ${clipTime}s (waited 10s)`);
          cleanup();
          resolve(); // Resolve anyway to avoid hanging the export
        }, 10000); // 10-second timeout for long videos

        const onSeeked = () => {
          cleanup();
          resolve();
        };

        const onError = (e: Event) => {
          cleanup();
          console.error(`Video seek error for ${clip.name}:`, e);
          resolve(); // Don't reject to avoid failing the whole export for one bad frame
        };

        const cleanup = () => {
          clearTimeout(timeout);
          video.removeEventListener('seeked', onSeeked);
          video.removeEventListener('error', onError);
        };

        video.addEventListener('seeked', onSeeked, { once: true });
        video.addEventListener('error', onError, { once: true });
      });
    }

    // CRITICAL: Wait for video to have current frame data ready before drawing
    // This is essential for WebM videos with transparency to render correctly
    // and for heavy long videos to not skip frames.
    const maxWaitForReady = 5000; // Increase to 5s for reliable frame capture
    const startWait = Date.now();

    // We wait for HAVE_CURRENT_DATA (2) or better, but ideally HAVE_FUTURE_DATA (3)
    // Also check that currentTime is reasonably close to target
    while (video.readyState < 3 && (Date.now() - startWait) < maxWaitForReady) {
      await new Promise(resolve => setTimeout(resolve, 20));
    }

    // FINAL CHECK: Check if video actually seeked successfully
    // Some browsers might report readyState >= 3 but still show previous frame if seeking is pending.
    // If the diff is too large (> 0.5s), the frame might be totally wrong.
    const timeDiff = Math.abs(video.currentTime - clipTime);
    if (timeDiff > 0.5) {
      console.warn(`[FrameRenderer] Video ${clip.name} timediff too high (${timeDiff.toFixed(3)}s) at ${clipTime}s. Retrying seek...`);
      video.currentTime = clipTime;
      // Short wait
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Check if video has sufficient data to draw
    if (video.readyState < 2) {
      console.warn(`Video ${clip.name} not ready (readyState: ${video.readyState}) at ${clipTime}s after waiting ${maxWaitForReady}ms`);
      return; // Skip drawing this frame to avoid black frame
    }

    // Apply transformations and draw
    this.applyTransformAndDraw(ctx, clip, video, currentTime, scaleMode);
  }

  /**
   * Render image clip
   */
  private renderImageClip(
    // FIX: Allow OffscreenCanvasRenderingContext2D to support WebCodecsExporter.
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clip: Clip,
    currentTime: number,
    scaleMode: 'fit' | 'fill' = 'fill'
  ): void {
    const mediaElement = this.mediaElements.get(clip.id);
    if (!mediaElement || !mediaElement.loaded) return;

    const img = mediaElement.element as HTMLImageElement;

    // Apply transformations and draw
    this.applyTransformAndDraw(ctx, clip, img, currentTime, scaleMode);
  }

  /**
   * Render text clip
   */
  private renderTextClip(
    // FIX: Allow OffscreenCanvasRenderingContext2D to support WebCodecsExporter.
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clip: Clip
  ): void {
    if (!clip.text || !clip.style) return;

    ctx.save();

    // Apply transform if present
    if (clip.transform) {
      this.applyTransform(ctx, clip.transform);
    }

    // Set text style (scale fontSize selon la résolution)
    const scaledFontSize = Math.round(clip.style.fontSize * this.fontScaleFactor);
    ctx.font = `${scaledFontSize}px ${clip.style.fontFamily}`;
    ctx.fillStyle = clip.style.color;
    ctx.textAlign = clip.style.textAlign;
    ctx.textBaseline = clip.style.textBaseline;

    // Draw text
    const x = this.canvasWidth / 2;
    const y = this.canvasHeight / 2;

    const lines = clip.text.split('\n');
    const lineHeight = scaledFontSize * 1.2;
    const totalHeight = lines.length * lineHeight;
    let startY = y;

    if (clip.style.textBaseline === 'middle') {
      startY = y - (totalHeight - lineHeight) / 2;
    } else if (clip.style.textBaseline === 'bottom') {
      startY = y - (totalHeight - lineHeight);
    }

    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], x, startY + (i * lineHeight));
    }

    ctx.restore();
  }

  /**
   * Apply transform and draw media
   */
  private applyTransformAndDraw(
    // FIX: Allow OffscreenCanvasRenderingContext2D to support WebCodecsExporter.
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clip: Clip,
    media: HTMLVideoElement | HTMLImageElement,
    currentTime: number,
    scaleMode: 'fit' | 'fill' = 'fill'
  ): void {
    ctx.save();

    // Get media dimensions
    // FIX: Use a type guard (`'videoWidth' in media`) to safely access properties of HTMLVideoElement.
    const mediaWidth = 'videoWidth' in media ? (media as any).videoWidth : (media as any).width;
    const mediaHeight = 'videoHeight' in media ? (media as any).videoHeight : (media as any).height;

    if (mediaWidth === 0 || mediaHeight === 0) {
      ctx.restore();
      return;
    }

    // Calculate scaling to fit/fill canvas
    const mediaAspect = mediaWidth / mediaHeight;
    const canvasAspect = this.canvasWidth / this.canvasHeight;

    let drawWidth, drawHeight;
    const shouldFill = scaleMode === 'fill';

    if (shouldFill) {
      // FILL (Cover) mode
      if (mediaAspect > canvasAspect) {
        // Media is wider - fit to height
        drawHeight = this.canvasHeight;
        drawWidth = drawHeight * mediaAspect;
      } else {
        // Media is taller - fit to width
        drawWidth = this.canvasWidth;
        drawHeight = drawWidth / mediaAspect;
      }
    } else {
      // FIT (Contain) mode
      if (mediaAspect > canvasAspect) {
        // Media is wider - fit to width
        drawWidth = this.canvasWidth;
        drawHeight = drawWidth / mediaAspect;
      } else {
        // Media is taller - fit to height
        drawHeight = this.canvasHeight;
        drawWidth = drawHeight * mediaAspect;
      }
    }

    // Center on canvas
    const x = (this.canvasWidth - drawWidth) / 2;
    const y = (this.canvasHeight - drawHeight) / 2;

    let animationScale = 1.0;
    const isAnimatedImage = clip.type === 'image' || (clip.type === 'video' && clip.url?.toLowerCase().endsWith('.gif'));
    if (isAnimatedImage) {
      const animDuration = 0.5;
      const timeIntoClip = currentTime - clip.start;
      const clipIsShort = clip.duration < animDuration * 2;
      const inDuration = clipIsShort ? clip.duration / 2 : animDuration;
      const outStartTime = clip.start + clip.duration - inDuration;

      if (currentTime < clip.start + inDuration && inDuration > 0) {
        animationScale = easeOutElastic(timeIntoClip / inDuration);
      } else if (currentTime > outStartTime) {
        const timeBeforeEnd = (clip.start + clip.duration) - currentTime;
        const easeInDuration = clip.start + clip.duration - outStartTime;
        if (easeInDuration > 0) {
          animationScale = 1 - easeInBack(1 - (timeBeforeEnd / easeInDuration));
        }
      }
    }

    // Apply custom transform if present
    if (clip.transform) {
      // Move to center
      ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);

      // Apply rotation
      ctx.rotate((clip.transform.rotation * Math.PI) / 180);

      // Apply scale
      ctx.scale(clip.transform.scale * animationScale, clip.transform.scale * animationScale);

      // Apply position offset
      ctx.translate(clip.transform.x, clip.transform.y);

      // Move back
      ctx.translate(-this.canvasWidth / 2, -this.canvasHeight / 2);
    }

    // Draw media
    // FIX: Cast media to `any` to satisfy `drawImage`'s `CanvasImageSource` parameter type.
    ctx.drawImage(media as any, x, y, drawWidth, drawHeight);

    ctx.restore();
  }

  /**
   * Apply transform to context
   */
  private applyTransform(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, transform: Transform): void {
    ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
    ctx.rotate((transform.rotation * Math.PI) / 180);
    ctx.scale(transform.scale, transform.scale);
    ctx.translate(transform.x, transform.y);
    ctx.translate(-this.canvasWidth / 2, -this.canvasHeight / 2);
  }

  // Max lines per subtitle (LVMH requirement: 1-2 lines max)
  private static readonly MAX_SUBTITLE_LINES = 2;

  /**
   * Word wrap text to fit within a max width, capped at MAX_SUBTITLE_LINES
   */
  private wrapText(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine === '' ? word : `${currentLine} ${word}`;
      if (ctx.measureText(testLine).width > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = word;
        // Cap at MAX_SUBTITLE_LINES
        if (lines.length >= FrameRenderer.MAX_SUBTITLE_LINES) {
          // Truncate remaining text into last line with ellipsis
          const remaining = words.slice(words.indexOf(word)).join(' ');
          if (remaining) {
            let lastLine = lines[lines.length - 1];
            lastLine = lastLine + ' ' + remaining;
            // Trim to fit maxWidth with ellipsis
            while (ctx.measureText(lastLine + '…').width > maxWidth && lastLine.length > 10) {
              lastLine = lastLine.slice(0, -1);
            }
            lines[lines.length - 1] = lastLine.trimEnd() + '…';
          }
          return lines;
        }
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }
    return lines;
  }

  /**
   * Render subtitles
   */
  public renderSubtitles(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clips: Clip[],
    currentTime: number,
    activeLanguage: string,
    style: SubtitleStyle
  ): void {
    // Find all active subtitles at current time
    const activeSubtitles: Subtitle[] = [];

    for (const clip of clips) {
      if (clip.subtitles && clip.subtitles[activeLanguage]) {
        const isClipActive = currentTime >= clip.start && currentTime < clip.start + clip.duration;
        if (!isClipActive) continue;

        // Subtitle timestamps are TIMELINE-ABSOLUTE (shifted via shiftAllSubtitleLanguages
        // when clips are moved on the timeline). So we compare directly against currentTime,
        // exactly as the preview does in TimelinePlayer.drawCanvasFrame.
        const clipSubtitles = clip.subtitles[activeLanguage].filter(
          sub => !sub.hidden && currentTime >= sub.start && currentTime < sub.end
        );
        activeSubtitles.push(...clipSubtitles);
      }
    }

    if (activeSubtitles.length === 0) return;

    // Render each subtitle
    for (const subtitle of activeSubtitles) {
      this.renderSubtitle(ctx, subtitle, style);
    }
  }

  /**
   * Render a single subtitle
   */
  private renderSubtitle(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    subtitle: Subtitle,
    style: SubtitleStyle
  ): void {
    ctx.save();

    // Scale subtitle size with output resolution to preserve visual proportion.
    const scaledFontSize = Math.max(1, Math.round(style.fontSize * this.fontScaleFactor));
    ctx.font = `bold ${scaledFontSize}px ${style.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    // Calculate position
    const x = this.canvasWidth / 2;
    const y = this.canvasHeight - (this.canvasHeight * style.positionY / 100);

    // Word wrap
    const paddingValue = style.padding ?? 5; // Fallback
    const padding = this.canvasWidth * (paddingValue / 100);
    const maxWidth = this.canvasWidth - (padding * 2);
    const lines = this.wrapText(ctx, subtitle.text, maxWidth);

    // Calculate total height
    const lineHeight = scaledFontSize * 1.2;
    const totalHeight = lines.length * lineHeight;

    // Draw each line
    lines.forEach((line, index) => {
      const lineY = y - totalHeight + (index + 1) * lineHeight;

      // Draw shadow or outline
      if (style.shadowType === 'shadow') {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = style.color;
        ctx.fillText(line, x, lineY);
      } else if (style.shadowType === 'outline') {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = style.outlineWidth ?? Math.max(1.5, scaledFontSize * 0.05);
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
        ctx.strokeText(line, x, lineY);
        ctx.fillStyle = style.color;
        ctx.fillText(line, x, lineY);
      } else {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.fillStyle = style.color;
        ctx.fillText(line, x, lineY);
      }
    });

    ctx.restore();
  }

  // FIX: Add missing cleanup method.
  /**
   * Cleanup resources
   */
  public cleanup(): void {
    this.mediaElements.clear();
  }
}
